import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { resolveFormValidationError } from "@/utils/formUtils";
import { tInterval } from "@/utils/translationUtils";
import { isDefined } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Card, Col, Divider, Form, Input, Row } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEnumeration } from "../../../../../../common/api/queries";
import { rowGutter } from "../../../../../../common/constants";
import { Permission } from "../../../../../../common/security/enums";
import { FieldConstraintViolation } from "../../../../../../common/types";
import type { UUID } from "../../../../../../typings/global";
import { useHasPermissions } from "../../../../../auth/hooks";
import ClientDrawerForm from "../../../../../client/components/drawers/ClientDrawerForm";
import ClientSearchInput from "../../../../../client/components/search/ClientSearchInput";
import { ClientFormStage, ClientSearchActionType } from "../../../../../client/enums";
import { useClientSearch } from "../../../../../client/hooks";
import { Client } from "../../../../../client/types";
import InstitutionSelect from "../../../../../enumerations/components/form/InstitutionSelect";
import ProductSelect from "../../../../../enumerations/components/form/ProductSelect";
import { EnumerationType } from "../../../../../enumerations/enums";
import { InstitutionType } from "../../../../../institution/enums";
import { ProductFinancialSector } from "../../../../../product/enums";
import { CreateUpdateLoanContract, LoanContract } from "../../../../types";
import { isContractVerified } from "../../../../utils";

interface Props {
  initialContract?: LoanContract;
  clients: (Partial<Client> | undefined)[];
  clientsViolationErrors: Map<number, FieldConstraintViolation[]>;
  onClientChange: (index: number, client?: Partial<Client>) => void;
  onClientViolationErrorsChange: (errorsMap: Map<number, FieldConstraintViolation[]>) => void;
}

const colSpan = 5;
const clientInputColSpan = 6;
const clientNameColSpan = 6;
const LOAN_CLIENT_INDEXES = [0, 1];

export const LoanContractFormHeaderSection = ({ initialContract, clients, ...props }: Props) => {
  const { t } = useTranslation();

  const clientSearch = useClientSearch();

  const productsEnum = useEnumeration(EnumerationType.PRODUCTS);
  const hasPrivilegedChangesPermission = useHasPermissions(Permission.PRIVILEGED_CHANGES_ON_VERIFIED_LOAN);

  const [clientFormOpen, setClientFormOpen] = useState(false);
  const [processedClientIndex, setProcessedClientIndex] = useState<number>();
  const [clientStages, setClientStages] = useState<ClientFormStage[]>(
    initialContract?.clients.map(() => ClientFormStage.SELECTED) || [],
  );

  const form = Form.useFormInstance<CreateUpdateLoanContract>();
  const institutionId = Form.useWatch("institutionId", form);

  const disablePrivilegedInputs = isContractVerified(initialContract) && !hasPrivilegedChangesPermission;

  const setClientStage = (index: number, stage?: ClientFormStage) => {
    if (stage) {
      const updatedClientStages = [...clientStages];
      updatedClientStages[index] = stage;
      setClientStages(updatedClientStages);
    }
  };

  useEffect(() => {
    if (
      isDefined(processedClientIndex) &&
      clientSearch.result.keyword === form.getFieldValue(["clientIdentifiers", processedClientIndex])
    ) {
      if (clientSearch.result.data) {
        setClientStage(processedClientIndex, ClientFormStage.EXISTING);
        props.onClientChange(processedClientIndex, clientSearch.result.data);
      } else {
        setClientStage(processedClientIndex, ClientFormStage.NEW);
      }
    }
  }, [clientSearch.result]);

  const handleInstitutionIdChange = (institutionId: UUID) => {
    const productId = form.getFieldValue("productId") as UUID;

    if (!productId) {
      return;
    }

    const product = productsEnum.find(
      (product) => product.id === productId && product.institutionIds.includes(institutionId),
    );

    if (!product) {
      form.setFieldsValue({ productId: undefined });
    }
  };

  const handleClientSearchActionClick = (index: number, actionType: ClientSearchActionType) => {
    switch (actionType) {
      case ClientSearchActionType.CREATE:
      case ClientSearchActionType.UPDATE:
        setProcessedClientIndex(index);
        setClientFormOpen(true);
        break;
      case ClientSearchActionType.DELETE:
        const updatedIdentifiers = [...(form.getFieldValue("clientIdentifiers") || [])];
        updatedIdentifiers[index] = undefined;
        form.setFieldsValue({ clientIdentifiers: updatedIdentifiers });

        const updatedClientStages = [...clientStages];
        // @ts-ignore ts-problem
        updatedClientStages[index] = undefined;
        setClientStages(updatedClientStages);

        deleteClientViolationErrors(index);
        setProcessedClientIndex(undefined);
        props.onClientChange(index);
        break;
    }
  };

  const handleClientSearchSubmit = (value: string, index: number) => {
    form
      .validateFields([["clientIdentifiers", index]])
      .then(() => {
        if (value) {
          clientSearch.onSearch({ keyword: value });
        }
      })
      .catch(resolveFormValidationError);
  };

  const handleClientSearchChange = (value: string, index: number) => {
    if (clientStages[index]) {
      setClientStage(index);
    }
    if (clients[index]) {
      props.onClientChange(index);
    }
    if (props.clientsViolationErrors.has(index)) {
      deleteClientViolationErrors(index);
    }

    handleClientSearchSubmit(value, index);
  };

  const handleClientFormSubmit = (client: Client, index: number) => {
    setClientFormOpen(false);
    setProcessedClientIndex(undefined);
    setClientStage(index, ClientFormStage.SELECTED);
    deleteClientViolationErrors(index);
    props.onClientChange(index, client);
  };

  const getClientFormIdentifier = (index: number): string => {
    return (form.getFieldValue("clientIdentifiers") || [])[index];
  };

  const deleteClientViolationErrors = (index: number) => {
    const updatedViolationsErrors = new Map<number, FieldConstraintViolation[]>([...props.clientsViolationErrors]);
    updatedViolationsErrors.delete(index);
    props.onClientViolationErrorsChange(updatedViolationsErrors);
  };

  const clientSearchProps = {
    processedType: processedClientIndex,
    violationErrors: props.clientsViolationErrors,
    inProgress: clientSearch.inProgress,
    inputColSpan: clientInputColSpan,
    clientNameColSpan,
    onActionClick: handleClientSearchActionClick,
    onFocus: setProcessedClientIndex,
    onSearch: handleClientSearchSubmit,
    onChange: handleClientSearchChange,
  };

  return (
    <>
      <Card type="inner" className="card-box margin-top-medium" title={t("contract.sections.contractIdentification")}>
        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name="contractNumber"
              label={t("contract.attrs.contractNumber")}
              dependencies={["secondaryContractNumber"]}
              rules={[
                validations.size(1, 64),
                validations.notNullIfOtherNull("secondaryContractNumber", t("contract.attrs.loanNumber")),
                validations.notEqualTo("secondaryContractNumber", t("contract.attrs.loanNumber")),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="secondaryContractNumber"
              label={t("contract.attrs.loanNumber")}
              dependencies={["contractNumber"]}
              rules={[
                validations.size(1, 64),
                validations.notNullIfOtherNull("contractNumber", t("contract.attrs.contractNumber")),
                validations.notEqualTo("contractNumber", t("contract.attrs.contractNumber")),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <InstitutionSelect
              formItemProps={{
                name: "institutionId",
                label: t("common.bankInstitution"),
                rules: [validations.notNull],
              }}
              selectProps={{ disabled: disablePrivilegedInputs, onChange: handleInstitutionIdChange }}
              optionsProps={{
                selected: initialContract ? [initialContract.institution] : undefined,
                filterType: InstitutionType.BANK,
              }}
            />
          </Col>

          <Col span={colSpan}>
            <ProductSelect
              formItemProps={{
                name: "productId",
                label: t("contract.attrs.productId"),
                rules: [validations.notNull],
              }}
              optionsProps={{
                institutionId,
                selectedInTree: {
                  selectedProductId: initialContract?.product.id,
                  condition: initialContract?.institution.id === institutionId,
                },
                hideAll: !institutionId,
                groupByProductGroup: true,
                filterSectors: [ProductFinancialSector.LOANS_AND_MORTGAGES],
              }}
              selectProps={{
                disabled: disablePrivilegedInputs,
                placeholder: !institutionId ? t("contract.helpers.loanProductPlaceholder") : undefined,
              }}
            />
          </Col>
        </Row>

        <Divider titlePlacement="start">{t("contract.sections.clients")}</Divider>

        <Row gutter={rowGutter}>
          {LOAN_CLIENT_INDEXES.map((clientIndex) => (
            <ClientSearchInput
              key={clientIndex}
              {...clientSearchProps}
              formItemProps={{
                name: ["clientIdentifiers", clientIndex],
                label: (
                  <LabelWithTooltip
                    tooltip={t("helpers.pinFormat")}
                    label={tInterval("contract.attrs.loanClientIdentifiers_interval", clientIndex)}
                  />
                ),
                rules: [
                  clientIndex === 0 ? validations.notBlank : validations.none,
                  validations.pinOrCrn,
                  validations.noRepeatedClient("clientIdentifiers"),
                ],
              }}
              optional={clientIndex > 0}
              formStage={clientStages[clientIndex]}
              formType={clientIndex}
              client={clients[clientIndex]}
            />
          ))}
        </Row>
      </Card>

      {isDefined(processedClientIndex) ? (
        <ClientDrawerForm<number>
          open={clientFormOpen}
          client={clients[processedClientIndex]}
          initialIdentifier={getClientFormIdentifier(processedClientIndex)}
          formType={processedClientIndex}
          violationErrors={props.clientsViolationErrors}
          onFormSubmit={handleClientFormSubmit}
        />
      ) : undefined}
    </>
  );
};
